import { featureText } from '../../feature-text';
import type { LandingPageFeature, LandingPageProduct } from '../../types';

interface PlanRow {
  id: string;
  text: string;
  tooltip?: string;
}

export interface PlanRows {
  /** The previous tier's name when this tier includes everything it has; the rows are then only the additions. */
  everythingIn?: string;
  rows: PlanRow[];
}

type Catalogue = Map<string, LandingPageFeature>;

function resolveRows(product: LandingPageProduct, catalogue: Catalogue): PlanRow[] {
  const seen = new Set<string>();

  return product.activeFeatures
    .filter(({ membershipFeatureId }) => {
      if (!catalogue.has(membershipFeatureId) || seen.has(membershipFeatureId)) return false;
      seen.add(membershipFeatureId);
      return true;
    })
    .map((active) => {
      const feature = catalogue.get(active.membershipFeatureId)!;
      const tooltipHtml = active.overrideTooltip ?? feature.defaultTooltip;

      return {
        id: active.membershipFeatureId,
        sortOrder: feature.sortOrder,
        text: featureText(
          active.overrideContent?.trim() ? active.overrideContent : feature.defaultContent,
        ),
        tooltip: tooltipHtml ? featureText(tooltipHtml) || undefined : undefined,
      };
    })
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(({ sortOrder: _sortOrder, ...row }) => row);
}

/**
 * Each tier lists only what the previous one lacks, under "Everything in <previous>" — but only
 * when it really has everything the previous one has. A tier that drops a feature lists in full.
 */
export function rollUpRows(
  products: LandingPageProduct[],
  features: LandingPageFeature[],
): PlanRows[] {
  const catalogue: Catalogue = new Map(features.map((f) => [f.membershipFeatureId, f]));
  let previous: { name: string; texts: Map<string, string> } | null = null;

  return products.map((product) => {
    const rows = resolveRows(product, catalogue);
    const texts = new Map(rows.map((row) => [row.id, row.text]));
    const isSuperset =
      previous !== null && [...previous.texts.keys()].every((id) => texts.has(id));
    const result: PlanRows =
      previous && isSuperset
        ? {
            everythingIn: previous.name,
            rows: rows.filter((row) => previous!.texts.get(row.id) !== row.text),
          }
        : { rows };

    previous = { name: product.name, texts };
    return result;
  });
}
